import { Component, Inject } from "@angular/core";
import { Router } from "@angular/router";
import { platform } from "../../../../platform/platform";
import { PLATFORM } from "../../../app.constants";
import { CommonSnackBarService } from "../../../_services/common-snack-bar.service";
import { NetworkStatusService } from "../../../_services/network-status.service";
import { OfflineDataSyncService } from "../../../_services/offline-data-sync.service";
import { SpinnerService } from "../../../_services/spinner.service";
import isOnlineStatus from "is-online";
import { AppInfoDbService } from "../../../_services/database/app-info-db.service";
import { LoginUserOfflineService } from "../../../_services/offline/login-user-offline.service";
import { IPC_CHANNEL } from "../../electron.constants";
import { IplexGConnectModeService } from "../../services/iplex-g-connect-mode.service";
import { ElectronService } from "../../../electron.service";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { SelectAppLaunchTypeConfirmDialogComponent } from "../dialog/select-app-launch-type-confirm-dialog/select-app-launch-type-confirm-dialog.component";
import { firstValueFrom, lastValueFrom } from "rxjs";
import { OfflineSetupService } from "../../services/offline-setup.service";
import { AuthService } from "@evc/platform";
import { RoutePage } from "../../../app-routes.enum";

@Component({
    selector: "app-network-overlay",
    templateUrl: "./network-overlay.component.html",
    styleUrls: ["./network-overlay.component.scss"],
    standalone: false
})
export class NetworkOverlayComponent {
  readonly disconnectMsg = "No Internet connection.";

  constructor(
    private router: Router,
    private networkStatusService: NetworkStatusService,
    private offlineDataSyncService: OfflineDataSyncService,
    private snackBarService: CommonSnackBarService,
    private oscSpinnerService: SpinnerService,
    private appInfoDbService: AppInfoDbService,
    private loginUserOfflineService: LoginUserOfflineService,
    private _electronService: ElectronService,
    @Inject(AuthService) private authService: AuthService,
    private deviceConnectModeService: IplexGConnectModeService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private offlineSetupService: OfflineSetupService
  ) {}

  get isOnline() {
    return this.networkStatusService.isOnline;
  }

  get isDeviceConnectMode() {
    return this.deviceConnectModeService.isHttpsConnectMode;
  }

  async changeToOffline() {
    if (platform.name === PLATFORM.DESKTOP) {
      try {
        console.debug("[NetworkOverlayComponent] changeToOffline() >>> Switch to offline - Start");

        this.authService.isOffline.set(true);
        await this.networkStatusService.switchOfflineMode();

        this.oscSpinnerService.show(true);

        if (await this.offlineDataSyncService.syncOnlineData()) {
          const isOnline = await this.networkStatusService.switchOfflineMode();

          if (isOnline) {
            this.snackBarService.open(
              this.translate.instant("offlineHeader.switchToOfflineError")
            );
          } else {
            await firstValueFrom(this.appInfoDbService.putNetworkMode(false));
            await this.router.navigate([RoutePage.InspectionList]);
          }
        }
      } catch (error) {
        console.debug("[NetworkOverlayComponent] changeToOffline() >>> Switch to offline - Error", error);
        throw error;
      } finally {
        console.debug("[NetworkOverlayComponent] changeToOffline() >>> Switch to offline - End");
      }
    }
  }

  async changeToOnline() {
    if (this.networkStatusService.isOnPremise) {
      const dialogRef = this.dialog.open(SelectAppLaunchTypeConfirmDialogComponent, { width: "400px" });

      const result = await firstValueFrom(dialogRef.afterClosed());

      if (result === true) {
        await this.switchToOnlineMode();
      }
    } else {
      await this.switchToOnlineMode();
    }
  }

  async switchToOnlineMode() {
    const isInternetConnect = await isOnlineStatus();
    if (platform.name === PLATFORM.DESKTOP && isInternetConnect) {
      try {
        console.debug("[NetworkOverlayComponent] changeToOnline() >>> Switch to online - Start");

        if (!this.router.url.includes(RoutePage.InspectionList))
          await this.router.navigate([RoutePage.InspectionList], {
            state: { switchOnline: true }
          });

        if (isInternetConnect) {
          const user = this.loginUserOfflineService.loginUser;

          this.authService.isOffline.set(false);

          this.oscSpinnerService.show(true);

          await this.router.navigate([RoutePage.InspectionList]);

          await this.networkStatusService.switchOnlineMode();
          await firstValueFrom(this.appInfoDbService.putSwitchOnlineBy(user.id));
          await firstValueFrom(this.appInfoDbService.putNetworkMode(true));
          await lastValueFrom(this.authService.login());
        } else {
          this.oscSpinnerService.hide(true);
          this.snackBarService.open(this.disconnectMsg);
        }
      } catch (error) {
        console.debug("[NetworkOverlayComponent] changeToOnline() >>> Switch to online - Error", error);
        throw error;
      } finally {
        this.oscSpinnerService.hide(true);
        console.debug("[NetworkOverlayComponent] changeToOnline() >>> Switch to online - End");
      }
    } else {
      this.snackBarService.open(
        this.translate.instant("offlineHeader.noInternetConnexionDetected")
      );
    }
  }
}
